import { useState } from 'react';
import { parseAmount } from '../utils/formatters';

const formatCurrencyValueForInput = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const numeric = parseFloat(value);
  return Number.isNaN(numeric) ? '' : numeric.toFixed(2);
};

const normalizeCurrencyValue = (value) => {
  if (value === '' || value === null || value === undefined) return '';

  const numeric = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(numeric)) return '';

  return parseFloat(numeric.toFixed(2));
};

const useCurrencyInputs = () => {
  const [editingValues, setEditingValues] = useState({});

  const getDisplayValue = (key, rawValue) => {
    if (editingValues[key] !== undefined) return editingValues[key];
    return formatCurrencyValueForInput(rawValue);
  };

  const handleFocus = (key, rawValue) => (event) => {
    setEditingValues(prev => {
      if (prev[key] !== undefined) return prev;
      return { ...prev, [key]: formatCurrencyValueForInput(rawValue) };
    });

    requestAnimationFrame(() => event.target.select());
  };

  const handleMouseUp = (event) => {
    event.preventDefault();
  };

  const handleChange = (key, rawValue, onCommit) => {
    const cleaned = rawValue.replace(/[^0-9.]/g, '');
    if (cleaned === '') {
      setEditingValues(prev => ({ ...prev, [key]: '' }));
      if (onCommit) onCommit('');
      return;
    }

    const [whole, ...decimals] = cleaned.split('.');
    const normalizedInput = decimals.length > 0 ? `${whole}.${decimals.join('')}` : whole;

    setEditingValues(prev => ({ ...prev, [key]: normalizedInput }));

    const numericValue = parseAmount(normalizedInput);
    if (onCommit) onCommit(Number.isNaN(numericValue) ? '' : numericValue);
  };

  const handleBlur = (key, rawValue, onCommit) => () => {
    const currentValue = editingValues[key] !== undefined
      ? editingValues[key]
      : formatCurrencyValueForInput(rawValue);

    const numericValue = parseFloat(currentValue);
    const finalized = Number.isNaN(numericValue) ? '' : numericValue.toFixed(2);

    setEditingValues(prev => {
      const next = { ...prev };
      if (finalized === '') {
        delete next[key];
      } else {
        next[key] = finalized;
      }
      return next;
    });

    if (onCommit) {
      onCommit(Number.isNaN(numericValue) ? '' : parseFloat(finalized));
    }
  };

  return {
    editingValues,
    setEditingValues,
    formatCurrencyValueForInput,
    normalizeCurrencyValue,
    getDisplayValue,
    handleFocus,
    handleMouseUp,
    handleChange,
    handleBlur
  };
};

export default useCurrencyInputs;
